<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { Building2, Check, ChevronsUpDown, Plus, Settings } from '@lucide/vue'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import CreateOrganizationDialog from '@/features/organizations/components/CreateOrganizationDialog.vue'
import { can } from '@/lib/permissions/can'
import { Permission } from '@/lib/permissions/keys'
import { useWorkspaceStore } from '@/stores/workspace'

const { t } = useI18n()
const router = useRouter()
const workspace = useWorkspaceStore()

const createOrganizationOpen = ref(false)
</script>

<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <button
        type="button"
        class="hover:bg-muted flex h-11 min-w-0 max-w-60 items-center gap-2 rounded-lg border px-3 text-left transition-colors"
      >
        <Building2 class="text-muted-foreground size-4 shrink-0" />
        <span class="min-w-0 leading-tight">
          <span class="text-muted-foreground block text-[10px]">{{
            t('navigation.organization')
          }}</span>
          <span class="block truncate text-sm font-medium">{{
            workspace.currentOrganization?.name
          }}</span>
        </span>
        <ChevronsUpDown class="text-muted-foreground ml-4 size-4 shrink-0" />
      </button>
    </DropdownMenuTrigger>
    <DropdownMenuContent class="w-64" align="end" :side-offset="6">
      <DropdownMenuLabel class="text-foreground text-sm font-semibold">{{
        t('navigation.yourOrganizations')
      }}</DropdownMenuLabel>
      <DropdownMenuItem
        v-for="org in workspace.organizations"
        :key="org.id"
        @select="workspace.setCurrentOrganization(org.id)"
      >
        <span class="min-w-0 flex-1 leading-tight">
          <span class="block truncate">{{ org.name }}</span>
          <span v-if="org.role" class="text-muted-foreground block text-[10px]">{{
            org.role
          }}</span>
        </span>
        <Check v-if="org.id === workspace.currentOrganizationId" class="size-4" />
      </DropdownMenuItem>
      <template v-if="workspace.currentOrganization">
        <DropdownMenuSeparator />
        <DropdownMenuItem @select="router.push('/organization/settings')">
          <Settings class="size-4" />
          {{ t('navigation.organizationSettings') }}
        </DropdownMenuItem>
      </template>
      <template v-if="can(Permission.OrganizationCreate, 'anywhere')">
        <DropdownMenuSeparator />
        <DropdownMenuItem @select="createOrganizationOpen = true">
          <Plus class="size-4" />
          {{ t('navigation.addOrganization') }}
        </DropdownMenuItem>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
  <CreateOrganizationDialog v-model:open="createOrganizationOpen" />
</template>
